'use client';
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getCookie } from 'cookies-next';
import { randomBytes } from 'crypto';
import getConfig from 'next/config';
import { getGQLClient } from '../gql/client';
import { getClientDefaultSessionTracing, logger } from '../logger/logger';
function buildApolloHeaders() {
  var _publicRuntimeConfig$, _publicRuntimeConfig$2;
  var _ref = getConfig() || {},
    _ref$publicRuntimeCon = _ref.publicRuntimeConfig,
    publicRuntimeConfig = _ref$publicRuntimeCon === void 0 ? {} : _ref$publicRuntimeCon;
  return {
    'apollographql-client-name': (_publicRuntimeConfig$ = publicRuntimeConfig.NEXT_PUBLIC_APP_NAME) !== null && _publicRuntimeConfig$ !== void 0 ? _publicRuntimeConfig$ : '',
    'apollographql-client-version': (_publicRuntimeConfig$2 = publicRuntimeConfig.NEXT_PUBLIC_APOLLO_CLIENT_VERSION) !== null && _publicRuntimeConfig$2 !== void 0 ? _publicRuntimeConfig$2 : '',
    Referer: "http://localhost:3000/",
    'X-Dev-Nonce': randomBytes(16).toString('base64')
  };
}
function getGraphQLEndpoint(config) {
  var _document, _process;
  var _ref2 = getConfig() || {},
    _ref2$publicRuntimeCo = _ref2.publicRuntimeConfig,
    publicRuntimeConfig = _ref2$publicRuntimeCo === void 0 ? {} : _ref2$publicRuntimeCo;
  var useProxyAPICookie = getCookie('useProxyAPI');
  var hostURLCookie = String(getCookie('hostURL'));
  if (useProxyAPICookie === true && hostURLCookie === ((_document = document) === null || _document === void 0 || (_document = _document.location) === null || _document === void 0 ? void 0 : _document.origin)) {
    var _document$location$or, _document2;
    return "".concat((_document$location$or = (_document2 = document) === null || _document2 === void 0 || (_document2 = _document2.location) === null || _document2 === void 0 ? void 0 : _document2.origin) !== null && _document$location$or !== void 0 ? _document$location$or : '', "/api/graphql");
  }
  var isServerSide = typeof window === 'undefined';
  var useServerEndpoint = ((_process = process) === null || _process === void 0 || (_process = _process.env) === null || _process === void 0 ? void 0 : _process[config.serverEnvVar]) === 'true' && isServerSide;
  if (useServerEndpoint) {
    var _process$env, _config$serverConfigK;
    return (_process$env = process.env[(_config$serverConfigK = config.serverConfigKey) !== null && _config$serverConfigK !== void 0 ? _config$serverConfigK : config.clientConfigKey]) !== null && _process$env !== void 0 ? _process$env : '';
  }
  return publicRuntimeConfig[config.clientConfigKey] || config.defaultUrl || '';
}
function executeGraphQLRequest(_x, _x2, _x3, _x4) {
  return _executeGraphQLRequest.apply(this, arguments);
}
function _executeGraphQLRequest() {
  _executeGraphQLRequest = _asyncToGenerator(function* (gqlTemplateString, options, config, gqlClient) {
    var _ref4 = getConfig() || {},
      _ref4$publicRuntimeCo = _ref4.publicRuntimeConfig,
      publicRuntimeConfig = _ref4$publicRuntimeCo === void 0 ? {} : _ref4$publicRuntimeCo;
    var baseHeaders = buildApolloHeaders();
    var requestHeaders = config.customHeadersBuilder ? config.customHeadersBuilder(baseHeaders) : baseHeaders;
    var url = getGraphQLEndpoint(config.endpointConfig);
    var client = gqlClient !== null && gqlClient !== void 0 ? gqlClient : getGQLClient();
    client.setEndpoint(url);
    if (config.enableErrorHandling) {
      var errorDetails = {
        errorMessage: '',
        params: {},
        endpoint: publicRuntimeConfig[config.endpointConfig.clientConfigKey],
        host: undefined
      };
      try {
        return yield client.request(gqlTemplateString, options, requestHeaders);
      } catch (e) {
        var _e$request;
        errorDetails.errorMessage = e.response;
        errorDetails.params = (_e$request = e.request) === null || _e$request === void 0 ? void 0 : _e$request.variables;
        console.error(JSON.stringify(errorDetails));
        throw e;
      }
    } else {
      return yield client.request(gqlTemplateString, options, requestHeaders);
    }
  });
  return _executeGraphQLRequest.apply(this, arguments);
}
export function graphQLRequest(_x5, _x6, _x7, _x8, _x9) {
  return _graphQLRequest.apply(this, arguments);
}
function _graphQLRequest() {
  _graphQLRequest = _asyncToGenerator(function* (gqlTemplateString, options, accessToken, proxyOptions, gqlClient) {
    return executeGraphQLRequest(gqlTemplateString, options, {
      endpointConfig: {
        serverEnvVar: 'NEXT_ENABLE_SERVER_APOLLO_GRAPHQL_ENDPOINT',
        serverConfigKey: 'NEXT_PUBLIC_SERVER_APOLLO_GRAPHQL_ENDPOINT',
        clientConfigKey: 'NEXT_PUBLIC_GRAPHQL_ENDPOINT',
        defaultUrl: 'http://localhost:3000/api/graphql'
      },
      enableErrorHandling: true,
      customHeadersBuilder: baseHeaders => {
        var headers = _objectSpread({}, baseHeaders);
        var accessTokenAuthorization = accessToken !== null && accessToken !== void 0 ? accessToken : proxyOptions === null || proxyOptions === void 0 ? void 0 : proxyOptions.accessToken;
        if (accessTokenAuthorization) {
          headers.Authorization = "Bearer ".concat(accessTokenAuthorization);
        }
        if (options !== null && options !== void 0 && options.idToken) {
          headers.Authorization = "Bearer ".concat(options.idToken);
          delete options.idToken;
        }
        if (proxyOptions !== null && proxyOptions !== void 0 && proxyOptions.basketIds) {
          headers['wb-basket-id'] = proxyOptions.basketIds;
        }
        return headers;
      }
    }, gqlClient);
  });
  return _graphQLRequest.apply(this, arguments);
}
export function useQueryRequest(queryKey, gqlTemplateString, options, reactQueryOptions, accessToken) {
  var returnError = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : false;
  var proxyOptions = arguments.length > 6 ? arguments[6] : undefined;
  var qOptions = queryOptions(_objectSpread({
    queryKey,
    queryFn: () => graphQLRequest(gqlTemplateString, options, accessToken, proxyOptions)
  }, reactQueryOptions));
  var _useQuery = useQuery(qOptions),
    isLoading = _useQuery.isLoading,
    isError = _useQuery.isError,
    isSuccess = _useQuery.isSuccess,
    isFetching = _useQuery.isFetching,
    data = _useQuery.data,
    error = _useQuery.error,
    refetch = _useQuery.refetch;
  if (isError) {
    var sessionTracing = getClientDefaultSessionTracing();
    logger.error({
      label: 'QUERY_REQUEST_ERROR',
      err: error,
      msg: _objectSpread({
        queryKey,
        accessToken
      }, sessionTracing)
    });
  }
  if (!returnError && isError) {
    throw error;
  }
  return {
    isLoading,
    isError,
    isSuccess,
    isFetching,
    data: data,
    error,
    refetch
  };
}
export function useMutationRequest(gqlTemplateString) {
  var returnError = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var accessToken = arguments.length > 2 ? arguments[2] : undefined;
  var reactQueryOptions = arguments.length > 3 ? arguments[3] : undefined;
  var mutation = useMutation(_objectSpread({
    mutationFn: options => graphQLRequest(gqlTemplateString, options, accessToken)
  }, reactQueryOptions));
  if (mutation.error) {
    var sessionTracing = getClientDefaultSessionTracing();
    logger.error({
      label: 'MUTATION_REQUEST_ERROR',
      err: mutation.error,
      msg: _objectSpread({
        accessToken
      }, sessionTracing)
    });
  }
  if (!returnError && mutation.isError) {
    throw mutation.error;
  }
  return {
    mutation: mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    isIdle: mutation.isIdle,
    data: mutation.data,
    error: mutation.error
  };
}
export function axiosRequest(_x0) {
  return _axiosRequest.apply(this, arguments);
}
function _axiosRequest() {
  _axiosRequest = _asyncToGenerator(function* (_ref3) {
    var method = _ref3.method,
      url = _ref3.url,
      data = _ref3.data,
      params = _ref3.params,
      headers = _ref3.headers;
    var _ref5 = getConfig() || {},
      _ref5$publicRuntimeCo = _ref5.publicRuntimeConfig,
      publicRuntimeConfig = _ref5$publicRuntimeCo === void 0 ? {} : _ref5$publicRuntimeCo;
    return axios({
      method,
      url,
      params,
      data,
      headers: _objectSpread({
        Origin: publicRuntimeConfig.NEXT_PUBLIC_ASSETS_URL_WITHOUT_BASIC
      }, headers)
    }).then(response => response.data);
  });
  return _axiosRequest.apply(this, arguments);
}
export function useRestQueryRequest(queryKey, method, url, headers, reactQueryOptions, body) {
  var qOptions = queryOptions(_objectSpread({
    queryKey,
    queryFn: () => axiosRequest({
      method,
      url,
      headers,
      data: body
    })
  }, reactQueryOptions));
  var _useQuery2 = useQuery(qOptions),
    isLoading = _useQuery2.isLoading,
    isError = _useQuery2.isError,
    isSuccess = _useQuery2.isSuccess,
    isFetching = _useQuery2.isFetching,
    data = _useQuery2.data,
    error = _useQuery2.error,
    refetch = _useQuery2.refetch;
  if (isError) {
    var sessionTracing = getClientDefaultSessionTracing();
    logger.error({
      label: 'REST_QUERY_REQUEST_ERROR',
      err: error,
      msg: _objectSpread({
        queryKey,
        url
      }, sessionTracing)
    });
  }
  return {
    isLoading,
    isError,
    isSuccess,
    isFetching,
    data: data,
    error,
    refetch
  };
}
export function useRestMutationRequest(url, method, headers) {
  var mutation = useMutation({
    mutationFn: data => axiosRequest({
      method,
      url,
      data,
      headers
    })
  });
  if (mutation.error) {
    var sessionTracing = getClientDefaultSessionTracing();
    logger.error({
      label: 'REST_MUTATION_REQUEST_ERROR',
      err: mutation.error,
      msg: _objectSpread({
        url
      }, sessionTracing)
    });
  }
  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    isIdle: mutation.isIdle,
    data: mutation.data,
    error: mutation.error,
    status: mutation.status
  };
}
export function graphQLRequestRestaurants(_x1, _x10, _x11, _x12, _x13) {
  return _graphQLRequestRestaurants.apply(this, arguments);
}
function _graphQLRequestRestaurants() {
  _graphQLRequestRestaurants = _asyncToGenerator(function* (gqlTemplateString, options, _accessToken, _proxyOptions, gqlClient) {
    return executeGraphQLRequest(gqlTemplateString, options, {
      endpointConfig: {
        serverEnvVar: 'NEXT_ENABLE_SERVER_RESTAURANTS_APOLLO_GRAPHQL_ENDPOINT',
        serverConfigKey: 'NEXT_PUBLIC_SERVER_RESTAURANTS_APOLLO_GRAPHQL_ENDPOINT',
        clientConfigKey: 'NEXT_PUBLIC_RESTAURANTS_GRAPHQL_ENDPOINT'
      },
      enableErrorHandling: false
    }, gqlClient);
  });
  return _graphQLRequestRestaurants.apply(this, arguments);
}
export function useQueryRequestRestaurants(queryKey, gqlTemplateString, options, accessToken, proxyOptions, reactQueryOptions) {
  var qOptions = queryOptions(_objectSpread({
    queryKey,
    queryFn: () => graphQLRequestRestaurants(gqlTemplateString, options, accessToken, proxyOptions)
  }, reactQueryOptions));
  var _useQuery3 = useQuery(qOptions),
    isLoading = _useQuery3.isLoading,
    isError = _useQuery3.isError,
    isSuccess = _useQuery3.isSuccess,
    isFetching = _useQuery3.isFetching,
    data = _useQuery3.data,
    error = _useQuery3.error,
    refetch = _useQuery3.refetch;
  if (isError) {
    var sessionTracing = getClientDefaultSessionTracing();
    logger.error({
      label: 'RESTAURANTS_QUERY_REQUEST_ERROR',
      err: error,
      msg: _objectSpread({
        queryKey,
        accessToken
      }, sessionTracing)
    });
  }
  return {
    isLoading,
    isError,
    isSuccess,
    isFetching,
    data,
    error,
    refetch
  };
}
export function useMutationRequestRestaurants(gqlTemplateString, options, reactQueryOptions) {
  var mutation = useMutation(_objectSpread({
    mutationFn: mutationOptions => graphQLRequestRestaurants(gqlTemplateString, mutationOptions)
  }, reactQueryOptions));
  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    isIdle: mutation.isIdle,
    data: mutation.data,
    error: mutation.error
  };
}